import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faTimes, faEdit, faSave, faArrowLeft, faCheckCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import './vqSearch.css';

const searchOptions = [
  { id: 'quote_date', label: 'Quote Date' },
  { id: 'quote_number', label: 'Quote Number' },
  { id: 'rfq_id', label: 'RFQ ID' },
  { id: 'req_number', label: 'REQ Number' },
  { id: 'req_date', label: 'REQ Date' },
  { id: 'customer_name', label: 'Customer' },
  { id: 'customer_location', label: 'Customer Location' },
  { id: 'customer_contact', label: 'Customer Contact' },
  { id: 'customer_email', label: 'Customer Email' }, 
  { id: 'customer_phone', label: 'Customer Phone' }, 
  { id: 'customer_mpn', label: 'Customer MPN' },
  { id: 'customer_mfr', label: 'Customer MFR' },
  { id: 'customer_target', label: 'Customer Target' },
  { id: 'ipn', label: 'Customer IPN' },
  { id: 'supplier_name', label: 'Supplier' },
  { id: 'supplier_location', label: 'Supplier Location' },
  { id: 'supplier_contact', label: 'Supplier Contact' },
  { id: 'supplier_email', label: 'Supplier Email' }, 
  { id: 'supplier_phone', label: 'Supplier Phone' }, 
  { id: 'supplier_mpn', label: 'Supplier MPN' },
  { id: 'supplier_mfr', label: 'Supplier MFR' },
  { id: 'supplier_price', label: 'Price' },
  { id: 'supplier_rating', label: 'Supplier Rating' },
  { id: 'demand_qty', label: 'Demand QTY' },
  { id: 'quote_qty', label: 'Quote QTY' },
  { id: 'supplier_qty', label: 'Available QTY' },
  { id: 'date_code', label: 'Date Code' },  
  { id: 'due_date', label: 'Due Date' },
  { id: 'lead_time', label: 'Lead Time' },
  { id: 'in_stock', label: 'In Stock' },
  { id: 'notes', label: 'Notes' },
  { id: 'supplier_source', label: 'Supplier Source' },
  { id: 'total_opportunity', label: 'Total Opportunity' },
  { id: 'profit_potential', label: 'Profit Potential' }, 
];

const formatNumericValue = (value, field) => {
  if (value === null || value === undefined) return '';
  
  // If it's already a string (pre-formatted from backend), return as is
  if (typeof value === 'string') return value;
  
  // For new numeric values, format according to field type
  if (typeof value === 'number') {
    switch(field) {
      case 'customer_target':
      case 'supplier_price':
        // Don't round these values - maintain full precision
        return Number(value).toFixed(4); // Keep 4 decimal places without rounding
      case 'profit_potential':
      case 'total_opportunity':
        return Math.round(value).toString(); // Display as whole numbers
      case 'quote_qty':
      case 'supplier_qty':
      case 'demand_qty':
        return value.toFixed(2);
      default:
        return value.toString();
    }
  }
  return value;
};

const VQSearch = ({ isVisible, onClose }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedOptions, setSelectedOptions] = useState(searchOptions.map(option => option.id));
  const [searchResults, setSearchResults] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectAll, setSelectAll] = useState(true);
  const [editingQuote, setEditingQuote] = useState(null);
  const [editedFields, setEditedFields] = useState({});
  const [showAutoUploaded, setShowAutoUploaded] = useState(false);
  const [showOnlyAutoUploaded, setShowOnlyAutoUploaded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [rfqData, setRfqData] = useState(null);
  const [isFetching, setIsFetching] = useState(false);


  useEffect(() => {
    const now = new Date();
    console.log('Current local date/time:', now.toString());
    console.log('Current UTC date/time:', now.toUTCString());

    const today = now.toLocaleDateString('en-CA');
    console.log('Formatted date (to be used):', today);
  
    setStartDate(today);
    setEndDate(today);

    console.log('StartDate set to:', today);
    console.log('EndDate set to:', today);
  
  }, []);

  useEffect(() => {
    console.log('selectAll:', selectAll);
    console.log('selectedOptions:', selectedOptions);
  }, [selectAll, selectedOptions]);

  /**
   * Date formatting standards across the application:
   * - Quote Date: Full timestamp (MM/DD/YYYY, HH:MM:SS AM/PM) - Used for submission/creation dates
   * - RFQ Date: Full timestamp (MM/DD/YYYY, HH:MM:SS AM/PM) - Used for RFQ submission dates
   * - Due Date: Short date only (Fri, 10 May 2024) - Used for deadlines/due dates
   */
  const formatDate = (dateString, fieldType = 'due_date') => {
    if (!dateString) return '';

    // No additional formatting needed for 'due_date' since it's preformatted from the backend
    if (fieldType === 'due_date') return dateString;
    
    const date = new Date(dateString);   
    // Full timestamp for other date fields
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: 'numeric', 
      minute: 'numeric', 
      second: 'numeric'
    });
  };
  
  const handleOptionChange = (optionId) => {
    setSelectedOptions(prevOptions => {
      const newOptions = prevOptions.includes(optionId)
      ? prevOptions.filter(id => id !== optionId)
      : [...prevOptions, optionId];
    setSelectAll(newOptions.length === searchOptions.length);
    return newOptions;
  });
};

const handleSelectAllChange = () => {
  setSelectAll(prevSelectAll => {
    const newSelectAll = !prevSelectAll;
    setSelectedOptions(newSelectAll ? searchOptions.map(option => option.id) : []);
    return newSelectAll;
  });
};

  const handleEdit = (quote) => {
    console.log('Original quote:', quote);
    console.log('customer_target value:', quote.customer_target);  
    
    // Store the raw values in editingQuote
    setEditingQuote(quote);
    setEditedFields({});
    
    if (quote.rfq_id) {
      fetchRfqData(quote.rfq_id);
    } else {
      setRfqData(null);
    }
  };

  const fetchRfqData = async (rfqId) => {
    setIsFetching(true);
    try {
      const response = await fetch(`https://us-central1-maia-tech.cloudfunctions.net/fetchRfqData?rfqId=${rfqId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch RFQ data');
      }
      const data = await response.json();
      console.log('Fetched RFQ data:', data);
      console.log('customer_target in RFQ data:', data.customer_target);
      console.log('typeof customer_target in RFQ:', typeof data.customer_target);
      
      setRfqData(data);

      // Only update fields that are empty in the current quote
      setEditedFields(prev => {
        const currentEdited = prev || {};
        const currentQuote = editingQuote || {};
        const newFields = {};

        // Never override these fields from RFQ data (supplier_source is not in this list)
        const preserveFields = [
          'supplier_name',
          'supplier_location',
          'supplier_contact',
          'supplier_price',
          'supplier_mpn',
          'supplier_mfr',
          'quote_qty',
          'supplier_qty',
          'profit_potential',
          'total_opportunity'
        ];

        // Only update fields if they're empty in both editedFields and current quote
        Object.keys(data).forEach(field => {
          if (preserveFields.includes(field)) {
            // Keep existing vendor quote data for these fields
            newFields[field] = currentEdited[field] || currentQuote[field];
          } else {
            const currentValue = currentEdited[field] || currentQuote[field];
            if (!currentValue || currentValue === '') {
              newFields[field] = data[field];
            } else {
              newFields[field] = currentValue;
            }
          }
        });

        console.log('Updated editedFields:', newFields);
        return { ...currentEdited, ...newFields };
      });

    } catch (error) {
      console.error('Error fetching RFQ data:', error);
      setErrorMessage('Failed to fetch RFQ data');
    } finally {
      setIsFetching(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedFields(prev => ({ ...prev, [field]: value }));
  };

  const handleFetchRfqData = () => {
    const rfqId = editedFields.rfq_id || editingQuote.rfq_id;
    if (rfqId) {
      fetchRfqData(rfqId);
    }
  };
  
  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      console.log('Editing Quote:', editingQuote);
      const quoteNumber = editingQuote.quote_number;
      console.log('Quote Number to be used:', quoteNumber);

      if (!quoteNumber) {
        console.error('Quote details:', editingQuote);
        throw new Error('Quote Number is missing.');
      }

      const updatedFields = { ...editedFields };
      if (rfqData) {
        updatedFields.rfq_id = rfqData.rfq_id;
      }
      console.log('Saving quote:', quoteNumber, updatedFields);

      const response = await fetch('https://us-central1-maia-tech.cloudfunctions.net/vqEditor', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          quote_number: quoteNumber,
          updatedFields: editedFields,
        }),
      });

      console.log('Response status:', response.status);
      const responseData = await response.json();
      console.log('Response data:', responseData);

      if (!response.ok) {
        throw new Error(responseData.error || `Failed to update quote. Server responded with status ${response.status}`);
      }

      // Use the processed quote data returned from the server
      const processedQuote = responseData.updatedQuote;
      console.log('Processed quote from server:', processedQuote);

      // Update the searchResults with the processed quote data
      setSearchResults(prevResults =>
        prevResults.map(result =>
          result.quote_number === quoteNumber
            ? { ...result, ...processedQuote }
            : result
        )
      );

      // Update editingQuote with the latest saved data
      setEditingQuote(prev => ({ ...prev, ...processedQuote }));
      
      // Perform a new search to refresh the results
      await handleSubmit();

    } catch (error) {
      console.error('Error updating quote:', error);
      setErrorMessage(`Failed to update quote: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleBackToResults = () => {
    setEditingQuote(null);
    setEditedFields({});
  };

  const handleSubmit = async () => {
    if (!startDate || !endDate) {
      setErrorMessage('Please select a date range.');
      return;
    }
  
    setIsSubmitting(true);
    setErrorMessage('');
  
    try {
      const response = await fetch('https://us-central1-maia-tech.cloudfunctions.net/vqSearch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          startDate: startDate,
          endDate: endDate,
          searchOptions: selectedOptions,
          searchKeyword: searchKeyword,
          showOnlyAutoUploaded: showOnlyAutoUploaded,
        }),
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const data = await response.json();
      setSearchResults(data);
  
      if (data.length === 0) {
        setErrorMessage('No results found for the given criteria.');
      }
    } catch (error) {
      console.error('Error fetching search results:', error);
      setErrorMessage(`An error occurred while fetching results: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="vq-search-modal">
      <div className="vq-search-content">
        <button className="close-button" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <h2>Vendor Quote Search</h2>
        <div className="search-container">
          <div className="search-options-container">
            <div className="date-range">
              <label>
                Start Date:
                <input 
                  type="date" 
                  value={startDate} 
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </label>
              <label>
                End Date:
                <input 
                  type="date" 
                  value={endDate} 
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </label>
            </div>
            <div className="keyword-search">
              <label>
                Search Keyword:
                <input 
                  type="text" 
                  value={searchKeyword} 
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  placeholder="Enter keyword..."
                />
              </label>
            </div>
            <div className="search-options">
              <label className="select-all">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAllChange}
                />
                Select All
              </label>
              {searchOptions.map((option) => (
                <label key={option.id}>
                  <input
                    type="checkbox"
                    checked={selectedOptions.includes(option.id)}
                    onChange={() => handleOptionChange(option.id)}
                  />
                  {option.label}
                </label>
              ))}
            </div>
            <button className="submit-button" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
          <div className="results-container">
            {!editingQuote && (
              <>
                <div className="results-header">
                  <div className="results-title-and-filter">
                    <h3>Search Results</h3>
                    <label className="auto-uploaded-filter">
                      <input
                        type="checkbox"
                        checked={showOnlyAutoUploaded}
                        onChange={(e) => setShowOnlyAutoUploaded(e.target.checked)}
                      />
                      Show Only Auto-Uploaded Quotes
                    </label>
                  </div>
                </div>
                {errorMessage && <p className="error-message">{errorMessage}</p>}
                {searchResults.length > 0 && (
                  <div className="search-results">
                    <table>
                      <thead>
                        <tr>
                          <th>
                            <span className="auto-uploaded-header">Auto-Uploaded</span>
                          </th>
                          <th>Action</th>
                          {selectedOptions.map(option => (
                            <th key={option}>{searchOptions.find(o => o.id === option)?.label || option}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {searchResults.map((result, index) => (
                          <tr key={index} className={result.auto_uploaded ? 'auto-uploaded-row' : ''}>
                            <td>
                              {result.auto_uploaded && (
                                <span className="auto-uploaded-indicator" title="Auto-Uploaded">
                                  <FontAwesomeIcon icon={faCheckCircle} />
                                </span>
                              )}
                            </td>
                            <td>
                              <button onClick={() => handleEdit(result)} className="edit-button">
                                <FontAwesomeIcon icon={faEdit} />
                              </button>
                            </td>
                            {selectedOptions.map(option => (
                            <td key={option}>
                              {option === 'in_stock' 
                                ? (result[option]?.toString() || '')
                                : formatNumericValue(result[option], option)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
            {editingQuote && (
              <div className="edit-quote">
                <button className="back-button" onClick={handleBackToResults}>
                  <FontAwesomeIcon icon={faArrowLeft} /> Back to Results
                </button>
                <h3>Edit Quote</h3>
                <form onSubmit={handleSave}>
                  <div className="form-group">
                    <label htmlFor="rfq_id">RFQ ID</label>
                    <div className="rfq-id-input">
                      <input
                        type="text"
                        id="rfq_id"
                        value={editedFields.rfq_id !== undefined ? editedFields.rfq_id : editingQuote.rfq_id || ''}
                        onChange={(e) => handleFieldChange('rfq_id', e.target.value)}
                      />
                      <button 
                        type="button" 
                        onClick={handleFetchRfqData}
                        disabled={isFetching}
                        className="fetch-rfq-button"
                      >
                        <FontAwesomeIcon icon={faSync} spin={isFetching} /> 
                        {isFetching ? 'Fetching...' : 'Fetch RFQ Data'}
                      </button>
                    </div>
                  </div>
                  {searchOptions.map((option) => (
                    option.id !== 'rfq_id' && (
                      <div key={option.id} className="form-group">
                        <label htmlFor={option.id}>{option.label}</label>
                        <input
                          type="text"
                          id={option.id}
                          value={(() => {
                            const value = editedFields[option.id] !== undefined 
                              ? editedFields[option.id] 
                              : editingQuote[option.id];
                            
                            // Handle boolean values for in_stock
                            if (option.id === 'in_stock') {
                              return value?.toString() || '';
                            }
                            
                            // Handle all date fields
                            if (['quote_date', 'due_date', 'req_date'].includes(option.id)) {
                              return value;  // 'due_date' is already formatted in supplyMatching
                            }
                            
                            // Handle numeric fields
                            if (['total_opportunity', 'profit_potential', 'supplier_price', 
                                 'customer_target', 'quote_qty', 'supplier_qty', 'demand_qty'].includes(option.id)) {
                                  console.log(`Edit form ${option.id} raw value:`, value);
                                  const formatted = formatNumericValue(value, option.id);
                                  console.log(`Edit form ${option.id} formatted value:`, formatted);                             
                                  return formatted;
                            }
                            
                            return value || '';
                          })()}
                          onChange={(e) => handleFieldChange(option.id, e.target.value)}
                        />
                      </div>
                    )
                  ))}
                  <button type="submit" className="save-button" disabled={isSaving}>
                    <FontAwesomeIcon icon={faSave} /> {isSaving ? 'Saving changes...' : 'Save Changes'}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
export default VQSearch;